import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { EASE } from "./motion";

const STORAGE_KEY = "bytevora-cookie-consent";

function Toggle({ label, desc, checked, disabled, onChange, testId }) {
  return (
    <div className="flex items-start justify-between gap-6 rounded-xl border border-slate-200 p-4">
      <div>
        <p className="text-sm font-semibold text-slate-900">{label}</p>
        <p className="mt-1 text-xs leading-relaxed text-slate-500">{desc}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        data-testid={testId}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition-colors duration-300 ${
          checked ? "bg-blue-600" : "bg-slate-200"
        } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-[left] duration-300 ${
            checked ? "left-[22px]" : "left-0.5"
          }`}
        />
      </button>
    </div>
  );
}

export default function CookieConsent() {
  const [bannerOpen, setBannerOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefs, setPrefs] = useState({ preferences: false, analytics: false });

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        const t = window.setTimeout(() => setBannerOpen(true), 1600);
        return () => window.clearTimeout(t);
      }
    } catch {
      setBannerOpen(true);
    }
  }, []);

  useEffect(() => {
    const open = () => {
      try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          setPrefs({ preferences: !!parsed.preferences, analytics: !!parsed.analytics });
        }
      } catch { /* keep defaults */ }
      setBannerOpen(false);
      setPrefsOpen(true);
    };
    window.addEventListener("bytevora:cookie-preferences", open);
    return () => window.removeEventListener("bytevora:cookie-preferences", open);
  }, []);

  const save = (value) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...value, necessary: true, ts: Date.now() }));
    } catch { /* storage unavailable */ }
    setBannerOpen(false);
    setPrefsOpen(false);
  };

  return (
    <>
      <AnimatePresence>
        {bannerOpen && (
          <motion.div
            data-testid="cookie-banner"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.55, ease: EASE }}
            className="fixed inset-x-4 bottom-4 z-[85] mx-auto max-w-[880px] md:bottom-6"
          >
            <div className="flex flex-col gap-5 rounded-2xl border border-slate-200 bg-white/95 p-6 shadow-[0_30px_70px_-30px_rgba(11,18,32,0.4)] backdrop-blur-xl md:flex-row md:items-center md:justify-between">
              <div className="max-w-[460px]">
                <p className="text-sm font-semibold text-slate-900">We use cookies</p>
                <p className="mt-1.5 text-[13px] leading-relaxed text-slate-500">
                  We use necessary cookies to operate our website. With your permission, we may
                  also use optional cookies to understand usage and improve your experience.{" "}
                  <Link to="/cookie-policy" className="font-medium text-blue-600 transition-colors hover:text-blue-700">
                    Cookie Policy
                  </Link>
                </p>
              </div>
              <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center">
                <button
                  type="button"
                  data-testid="cookie-reject-button"
                  onClick={() => save({ preferences: false, analytics: false })}
                  className="rounded-[10px] border border-slate-200 px-4 py-2.5 text-[13px] font-semibold text-slate-700 transition-colors duration-200 hover:border-slate-400"
                >
                  Reject Non-Essential
                </button>
                <button
                  type="button"
                  data-testid="cookie-manage-button"
                  onClick={() => {
                    setBannerOpen(false);
                    setPrefsOpen(true);
                  }}
                  className="rounded-[10px] border border-slate-200 px-4 py-2.5 text-[13px] font-semibold text-slate-700 transition-colors duration-200 hover:border-slate-400"
                >
                  Manage Preferences
                </button>
                <button
                  type="button"
                  data-testid="cookie-accept-button"
                  onClick={() => save({ preferences: true, analytics: true })}
                  className="rounded-[10px] bg-navy px-4 py-2.5 text-[13px] font-semibold text-white transition-colors duration-300 hover:bg-blue-600"
                >
                  Accept All
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {prefsOpen && (
          <motion.div
            data-testid="cookie-preferences-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] flex items-end justify-center bg-navy/40 p-4 backdrop-blur-sm sm:items-center"
            onClick={() => setPrefsOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 32, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.4, ease: EASE }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-[520px] rounded-2xl border border-slate-200 bg-white p-7 shadow-2xl"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-bold tracking-tight text-slate-900">Cookie Preferences</h2>
                  <p className="mt-1 text-[13px] text-slate-500">
                    Choose which optional cookies this website may use.
                  </p>
                </div>
                <button
                  type="button"
                  data-testid="cookie-preferences-close"
                  aria-label="Close cookie preferences"
                  onClick={() => setPrefsOpen(false)}
                  className="grid h-9 w-9 place-items-center rounded-[10px] border border-slate-200 text-slate-500 transition-colors hover:border-slate-400 hover:text-slate-900"
                >
                  <X size={15} />
                </button>
              </div>
              <div className="mt-6 space-y-3">
                <Toggle
                  label="Necessary"
                  desc="Required for security, session and core website functionality. Always active."
                  checked
                  disabled
                  onChange={() => {}}
                  testId="cookie-toggle-necessary"
                />
                <Toggle
                  label="Preferences"
                  desc="Remember choices such as display or cookie settings."
                  checked={prefs.preferences}
                  onChange={(v) => setPrefs((p) => ({ ...p, preferences: v }))}
                  testId="cookie-toggle-preferences"
                />
                <Toggle
                  label="Analytics"
                  desc="Help us understand how visitors use the website so we can improve it."
                  checked={prefs.analytics}
                  onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                  testId="cookie-toggle-analytics"
                />
              </div>
              <div className="mt-7 flex flex-col gap-2.5 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  data-testid="cookie-save-reject-button"
                  onClick={() => save({ preferences: false, analytics: false })}
                  className="rounded-[10px] border border-slate-200 px-5 py-2.5 text-[13px] font-semibold text-slate-700 transition-colors duration-200 hover:border-slate-400"
                >
                  Reject Non-Essential
                </button>
                <button
                  type="button"
                  data-testid="cookie-save-preferences-button"
                  onClick={() => save(prefs)}
                  className="rounded-[10px] bg-navy px-5 py-2.5 text-[13px] font-semibold text-white transition-colors duration-300 hover:bg-blue-600"
                >
                  Save Preferences
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
